



































import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import Axios from 'axios';
import { SVG, Element } from '@svgdotjs/svg.js';
import '@svgdotjs/svg.panzoom.js';
import IColors from '@/models/colors';
import { IWorldCountry } from '../models/world';
import { HexToHSL, GetHSLObject, HSLToHex, darkenBy20 } from '../helpers/color-helpers';

@Component({})
export default class WorldMapComponent extends Vue {

	public loaded = {
		loadSvg: false,
		mounted: false,
	};

	public tooltip = {
		text: '',
		show: false,
		canceled: false,
		x: 0,
		y: 0,
	};

	public get colors(): IColors {
		return this.$store.getters['world/getColors'];
	}

	public get countries(): IWorldCountry[] {
		return this.$store.getters['world/getSummary'].countries;
	}

	public get grades(): number[] {
		return this.$store.getters['world/getSummary'].grades;
	}
	public get gradesArr(): Array<({ text: string, val: number })> {
		function flattenDeep(arr1) {
			return arr1.reduce((acc, val) => Array.isArray(val) ? acc.concat(flattenDeep(val)) : acc.concat(val), []);
		}
		const arr = this.grades.map((v, i, a) => {
			if (i === 0) {
				return { text: `> ${v.toLocaleString()}`, val: v + 1 };
			} else if (i === a.length - 1) {
				return [{ text: `${a[i].toLocaleString()} - ${a[i - 1].toLocaleString()}`, val: v }, { text: `< ${v.toLocaleString()}`, val: -1 }];
			} else {
				return { text: `${a[i].toLocaleString()} - ${a[i - 1].toLocaleString()}`, val: v };
			}
		});
		return flattenDeep(arr);
	}

	public get activeCodes(): string[] {
		return this.$store.getters['world/getActiveCountries'];
	}

	public hoveredId = '';

	public get selectedCountry(): string {
		return this.$store.getters['world/getCountryId'];
	}
	public created() {
		Axios.get('/svg/world.svg')
			.then((r) => {
				const loadedSvg = new DOMParser()
					.parseFromString(r.data, 'image/svg+xml')
					.getElementsByTagName('svg')[0];
				(this.$refs.svg as SVGElement).outerHTML = loadedSvg.outerHTML;
				this.loaded.loadSvg = true;
			});
	}

	public mounted() {
		this.loaded.mounted = true;
	}

	@Watch('activeCodes', { deep: true })
	private onChangeCodes() {
		if (this.loaded.loadSvg && this.loaded.mounted) {
			document.querySelectorAll('#worldmap svg path').forEach((v) => {
				if (this.activeCodes.includes(v.id)) {
					const color = this.setCountryColor(v.id);
					v.setAttribute('fill', color);
				} else {
					v.setAttribute('fill', this.colors.nodata);
				}
				v.setAttribute('stroke', this.colors.border);
			});
		}
	}

	@Watch('loaded', { deep: true })
	private onLoadChanged(
		val: ({ loadSvg: boolean, mounted: boolean }),
		oldVal: ({ loadSvg: boolean, mounted: boolean })) {
		if (val.loadSvg && val.mounted) {
			document.querySelectorAll('#worldmap svg path').forEach((v) => {
				if (this.activeCodes.includes(v.id)) {
					const color = this.setCountryColor(v.id);
					v.setAttribute('fill', color);
				} else {
					v.setAttribute('fill', this.colors.nodata);
				}
				v.setAttribute('stroke', this.colors.border);
			});
			const svg = document
				.getElementById('worldmap')!
				.getElementsByTagName('svg')[0];
			svg.addEventListener('mouseover', this.onMouseOver);
			svg.addEventListener('mouseout', this.onMouseOut);
			svg.addEventListener('click', this.onClick);
			(SVG('#worldmap svg') as any).panZoom({ zoomFactor: 0.15, zoomMin: 1, zoomMax: 4 });
		}
	}


	private onMouseOver(event: MouseEvent) {
		if (event.target && (event.target as HTMLElement).tagName === 'path') {
			const targetElem = event.target as HTMLElement;
			if (this.activeCodes.includes(targetElem.id)) {
				this.hoveredId = targetElem.id;
				this.tooltip.canceled = false;
				this.tooltip.x = event.x;
				this.tooltip.y = event.y;
				this.tooltip.text = this.countries.find((v) => v.code === this.hoveredId)?.name!;
			}
		} else if (event.target && ((event.target as HTMLElement).tagName === 'svg')) {
			this.tooltip.canceled = true;
			setTimeout(() => {
				if (this.tooltip.canceled) {
					this.tooltip.show = false;
				}
			}, 100);
		}
	}

	private onClick(event: Event) {
		if (event.target && (event.target as HTMLElement).tagName === 'path') {
			const targetElem = event.target as HTMLElement;
			if (this.activeCodes.includes(targetElem.id)) {
				if (this.selectedCountry === targetElem.id) {
					this.$store.dispatch('world/updateCountryId', '');
				} else {
					this.$store.dispatch('world/updateCountryId', targetElem.id);
				}
			}
		} else if (event.target && (event.target as HTMLElement).tagName === 'svg') {
			this.$store.dispatch('world/updateCountryId', '');
		}
	}

	private onMouseOut(event: MouseEvent) {
		this.hoveredId = '';
	}

	@Watch('selectedCountry')
	private onCountryChange(val: string, oldVal: string) {
		const svgObj = SVG('#worldmap svg');
		const paths = svgObj
			.children()
			.filter((c) => c.type === 'path' && this.activeCodes.includes(c.id()))
			.forEach((p) => {
				if (p.id() === val) {
					p.attr({ fill: darkenBy20(this.colors.hover) });
				} else {
					if (p.fill() === darkenBy20(this.colors.hover)) {
						const color = this.setCountryColor(p.id());
						p.attr({ fill: color, stroke: this.colors.border });
					}
				}
			});
	}

	@Watch('hoveredId')
	private onCountryHover(val: string, oldVal: string) {
		const svgObj = SVG('#worldmap svg');
		const paths = svgObj
			.children()
			.filter((c) => c.type === 'path' && this.activeCodes.includes(c.id()))
			.forEach((p) => {
				const currentColor = p.fill();
				if (p.id() === val) {
					const color = this.setCountryColor(val);
					if (currentColor === color) {
						p.attr({ fill: this.colors.hover, stroke: darkenBy20(this.colors.border) });
						this.tooltip.show = true;
					}
				} else {
					if (currentColor === this.colors.hover) {
						const color = this.setCountryColor(p.id());
						p.attr({ fill: color, stroke: this.colors.border });
					}
				}
			});

	}

	private setCountryColor(code: string) {
		const countryObj = this.countries.find((v) => v.code === code);
		if (countryObj) {
			const total = countryObj.total;
			const gradeCount = this.gradesArr.length;
			const hslObj = GetHSLObject(HexToHSL(this.colors.default));
			const step = parseInt(Math.round((80 / gradeCount)).toFixed(1), 10);
			const index = this.gradesArr.findIndex((v) => total >= v.val);
			hslObj.l = hslObj.l - (step * (gradeCount - index - 1));
			return HSLToHex(hslObj);
		} else {
			return this.colors.nodata;
		}
	}

	private colorGrade(grade: string) {
		const gradeCount = this.gradesArr.length;
		const hslObj = GetHSLObject(HexToHSL(this.colors.default));
		const step = parseInt(Math.round((80 / gradeCount)).toFixed(1), 10);
		const index = this.gradesArr.findIndex((v) => v.text === grade);
		hslObj.l = hslObj.l - (step * (gradeCount - index - 1));
		return HSLToHex(hslObj);
	}
}
